import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { LogIn, LogOut } from 'lucide-react';
import { QuickPunchModal } from './QuickPunchModal';

const API = process.env.REACT_APP_BACKEND_URL;

// Compact, live clock-status pill with an inline one-tap Clock In/Out button.
export const ClockStatusPill = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [nowMs, setNowMs] = useState(Date.now());
  const [punchType, setPunchType] = useState(null); // 'in' | 'out' → opens modal
  const baseRef = useRef({ seconds: 0, at: Date.now(), running: false });

  const getToken = () => localStorage.getItem('portal_token') || localStorage.getItem('token');

  const load = async () => {
    try {
      const res = await axios.get(`${API}/api/clock/portal/my-status`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setStatus(res.data);
      baseRef.current = {
        seconds: res.data.worked_seconds_today || 0,
        at: Date.now(),
        running: !!res.data.clocked_in,
      };
    } catch {
      setStatus({ clocked_in: false, worked_seconds_today: 0 });
    }
  };

  useEffect(() => {
    load();
    const refresh = setInterval(load, 60000); // resync with server each minute
    const tick = setInterval(() => setNowMs(Date.now()), 1000);
    return () => { clearInterval(refresh); clearInterval(tick); };
  }, []);

  if (!status) return null;

  const { seconds, at, running } = baseRef.current;
  const live = Math.max(0, Math.floor(seconds + (running ? (nowMs - at) / 1000 : 0)));
  const h = Math.floor(live / 3600);
  const m = Math.floor((live % 3600) / 60);
  const s = live % 60;
  const pad = (n) => String(n).padStart(2, '0');
  const nothingToday = !running && live === 0;

  const goRecords = () => navigate('/employee-portal/clock-records');

  return (
    <>
      <div
        data-testid="clock-status-pill"
        className={`group w-full flex items-center justify-between gap-2 rounded-2xl px-4 py-2.5 transition-all ${
          running
            ? 'bg-gradient-to-r from-emerald-500 to-teal-600 shadow-lg shadow-emerald-500/25'
            : nothingToday
            ? 'bg-white border border-gray-200'
            : 'bg-gradient-to-r from-slate-700 to-slate-800 shadow-md'
        }`}
      >
        {/* Status + time (tap to open records) */}
        <button
          type="button"
          onClick={goRecords}
          data-testid="clock-status-open-records"
          className="flex items-center gap-2.5 min-w-0 flex-1 text-left active:scale-[0.99] transition-transform"
        >
          <span className="relative flex h-2.5 w-2.5 shrink-0">
            {running && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-70" />}
            <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${running ? 'bg-white' : nothingToday ? 'bg-gray-300' : 'bg-slate-300'}`} />
          </span>
          <div className="leading-tight min-w-0">
            <p className={`text-[10px] font-semibold uppercase tracking-wider ${running ? 'text-emerald-50' : nothingToday ? 'text-gray-400' : 'text-slate-300'}`}>
              {running ? 'Clocked In' : nothingToday ? 'Not clocked in' : 'Worked today'}
            </p>
            {!nothingToday ? (
              <div className="flex items-baseline gap-1 font-mono tabular-nums">
                <span className="text-xl font-bold leading-none text-white">{h}</span>
                <span className={`text-[10px] font-medium ${running ? 'text-emerald-100' : 'text-slate-300'}`}>h</span>
                <span className="text-xl font-bold leading-none text-white">{pad(m)}</span>
                <span className={`text-[10px] font-medium ${running ? 'text-emerald-100' : 'text-slate-300'}`}>m</span>
                {running && <span className="text-[10px] font-medium text-emerald-100/80 ml-0.5">{pad(s)}s</span>}
              </div>
            ) : (
              <p className="text-[11px] text-gray-400">Tap the button to start your shift</p>
            )}
          </div>
        </button>

        {/* Inline one-tap action → opens selfie/geo verification modal */}
        <button
          type="button"
          onClick={() => setPunchType(running ? 'out' : 'in')}
          data-testid={running ? 'quick-clock-out-btn' : 'quick-clock-in-btn'}
          className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-semibold transition-all active:scale-95 ${
            running
              ? 'bg-white text-emerald-700 hover:bg-emerald-50 shadow-sm'
              : nothingToday
              ? 'bg-sky-600 text-white hover:bg-sky-700 shadow-sm'
              : 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-sm'
          }`}
        >
          {running ? <LogOut className="h-3.5 w-3.5" /> : <LogIn className="h-3.5 w-3.5" />}
          {running ? 'Clock Out' : 'Clock In'}
        </button>
      </div>

      <QuickPunchModal
        open={!!punchType}
        type={punchType}
        status={status}
        onClose={() => setPunchType(null)}
        onDone={() => { setPunchType(null); load(); }}
      />
    </>
  );
};

export default ClockStatusPill;
